import { events } from "@constants/tracking";
import { AppBar } from "@features/dashboard/layouts/AppBar";
import { buildType } from "@helpers/app-identity";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import useExternalAuthFlow from "@hooks/useExternalAuthFlow";
import { composeMessage } from "@memorang/helpers/email";
import { getPlatform } from "@memorang/helpers/platform";
import Box from "@memorang/ui/components/Box";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import EmptyState from "@memorang/ui/components/EmptyView";
import LoaderDialog from "@memorang/ui/components/LoaderDialog";
import { useDevice } from "@memorang/ui/useDevice";
import { router } from "expo-router";
import { Button } from "react-native-paper";
import AccessDeniedImage from "../../assets/undraw_access_denied.svg";

const ExternalAuthGate = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const {
		isLoading,
		messages,
		showError,
		email,
		isAccessRestricted,
		setIsAccessRestricted,
	} = useExternalAuthFlow();
	const { isMobile } = useDevice();

	const supportEmail = useAppStore((store) => store.supportEmail);

	const { explicitCallTrackCustomEvent } = useAnalytics();

	const handleContactSupport = () => {
		if (!email) {
			return;
		}
		explicitCallTrackCustomEvent({
			eventName: events.contactSupportClicked,
			email,
		});
		const platform = getPlatform();
		const subject = `${buildType} Support | No Access | ${platform} | ${email}`;
		const body = `Hi, my account email is ${email} and I'm reaching out because:`;

		composeMessage(subject, body, supportEmail);
	};

	const AccessRestrictedActions = () => {
		const secondaryCta = "Choose exam";
		const primaryCta = "Contact support";

		return (
			<Box gap={8} flexDirection="row" alignItems="center">
				<Button onPress={() => router.replace("/onboarding")}>
					{secondaryCta}
				</Button>
				<Button mode="contained" onPress={handleContactSupport}>
					{primaryCta}
				</Button>
			</Box>
		);
	};

	if (isLoading) {
		return (
			<>
				{!isMobile && <AppBar title="" variant="public" showLogo />}
				<LoaderDialog messages={messages} visible />
			</>
		);
	}

	if (isAccessRestricted) {
		const handleDismiss = () => {
			setIsAccessRestricted(false);
			router.replace("/home");
		};

		return (
			<ConfirmDialog
				visible
				title="Access restricted"
				body="You are not authorized to access this content. If you believe this is an error, please contact support."
				maxWidth="xs"
				icon="shield-alert-outline"
				dismissCallback={handleDismiss}
				actions={<AccessRestrictedActions />}
			/>
		);
	}

	if (showError) {
		return (
			<Box flex={1} id="auth-external-error">
				<EmptyState
					title="Unable to authenticate"
					subtitle="We are sorry, but we are unable to authenticate you. Go to login to continue."
					image={AccessDeniedImage}
					cta="Go to login"
					handleAction={() => {
						router.replace("/login");
					}}
				/>
			</Box>
		);
	}

	return children;
};

export default ExternalAuthGate;
